import { ready } from "../../../core";
import { browser } from "$lib/core/js/util/env";
import { createHtml, isFormElement, stringEquals } from "$lib/core";

export * from "./restructure";

export const fillableElements = ["input", "textarea", "select"].join(",");
export const baylorFormClass = "bu-form";

/**
 * Runs logic on page load once for every Baylor Form on the page.
 * 
 * @param {(form: HTMLFormElement) => void} callback 
 * @param {string|null} [formSelector=null]
 * @returns {void}
 */
export function forEachForm(callback, formSelector = null) {
  if (!browser) return;
  ready(() => {
    [...document.querySelectorAll(formSelector ?? `.${baylorFormClass}`)].filter(isFormElement).forEach(callback);
  });
}

/**
 * @param {HTMLElement} field An HTMLElement that is classified as a "field" whether by a `.field` or `.fieldgroup` selector (or other?).
 * @returns {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]}
 */
export function getFieldFillableElements(field) {
  return /** @type {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]} */ ([...field.querySelectorAll(`:scope :is(${fillableElements})`)]);
}

/**
 * @param {"select" | "checkboxes" | "checkbox" | "autocomplete"} type
 * @param {string} name
 * @param {number} index
 * @param {import("../../public").SelectListItem} item
 * @returns {HTMLElement}
 */
export function createOption(type, name, index, item) {
  let disabled = "";
  let ariaDisabled = "";
  if (typeof(item.disabled) === "boolean") {
    disabled = item.disabled ? "disabled " : "";
    ariaDisabled = item.disabled
      ? `aria-disabled="true" `
      : `aria-disabled="false" `;
  }
  let selected = "";
  if (item.selected) {
    switch (type) {
      case "select":
        selected = `selected="selected" `;
        break;
      case "checkboxes":
      case "checkbox":
        selected = `checked="checked" `;
        break;
      default:
        break;
    }
  }
  const id = `${name.replaceAll(".", "_")}_${index}`;
  let html;
  if (type === "select" || type === "autocomplete") {
    html = `
      <option id="${id}" value="${item.value.trim()}" ${disabled}${ariaDisabled}${selected}>${item.text}</option>
    `;
  } else {
    html = `
      <div class="form-item">
        <input data-seq-id="${index}" type="checkbox" id="${id}" name="${name}" value="${item.value.trim()}" ${disabled}${ariaDisabled}${selected}/>
        <label for="${id}" class="option">${item.text}</label>
      </div>
    `;
  }
  const div = createHtml(html);
  return div;
}

/**
 * Confirms that an <input> element has the "checkbox" type.
 * @param {any} item
 * @returns {item is HTMLInputElement}
 */
export function isCheckbox(item) {
    return item instanceof HTMLInputElement && item.type === "checkbox";
}

/**
 * Confirms whether a string matches a variety of single-character boolean strings or the true/false strings.
 * @param {string} value
 * @returns {boolean|undefined}
 */
export function indicator2Bool(value) {
  if (value == null || value === "") return undefined;
  if (["y", "Y", "1", "t", "T"].some(v => v === value) || stringEquals(value, "true")) return true;
  if (["n", "N", "0", "f", "F"].some(v => v === value) || stringEquals(value, "false")) return false;
  return undefined;
}

/**
  * @param {HTMLSelectElement} select A select element with either 2 options ["Yes", "No"] or 3 options ["", "Yes", "No"] ("Yes" and "No" can be True/False, true/false, 1/0, etc.).
  * @param {any} value
  */
export function updateYesNoSelect(select, value) {
  const v = indicator2Bool(value);
  if (v === undefined) {
    select.value = "";
  } else if (select.options.length > 3) {
    console.warn("Cannot process YesNo select: contains more than 3 options.", select, Array.from(select.options));
  } else if (select.options.length < 2) {
    console.warn("Cannot process YesNo select: contains less than 2 options.", select, Array.from(select.options));
  } else {
    let yes = 0;
    let no = 1;
    if (select.options.length === 3) {
      yes = 1;
      no = 2;
    }
    select.value = (v ? select.options.item(yes)?.value : select.options.item(no)?.value) ?? "";
  }
}

/**
  * @param {HTMLFormElement} form
  * @param {string} name
  * @param {((control: import("$lib/core/js/util/defs").FormControl) => boolean)|null} [filter=null]
  * @returns {(HTMLElement & {value: string, disabled: boolean, type?: string, checked?: boolean})[]} The form controls within the form matching the name.
  */
export function getNamedControls(form, name, filter = null) {
  const elements = /** @type {(HTMLElement & {value: string, disabled: boolean, type?: string, checked?: boolean})[]} */ ([]);
  const item = form.elements.namedItem(name);
  if (item instanceof HTMLElement) {
    const control = /** @type {import("$lib/core/js/util/defs").FormControl} */ (item);
    if (filter == null || filter(control)) {
      elements.push(control);
    }
  } else if (item instanceof RadioNodeList) {
    for (const node of item) {
      const control = /** @type {import("$lib/core/js/util/defs").FormControl} */ (node);
      if (filter == null || filter(control)) {
        elements.push(control);
      }
    }
  }
  return elements;
}

/**
  * Prefills an element with the given value. Disabled fields are skipped. <input> elements of type "checkbox" & <select> elements use indicator2Bool conversions, updating their "checked" status or selecting the corresponding option.
  * 
  * @param {HTMLFormElement} form
  * @param {HTMLElement|string} target The form control to load or its name (which recursively loads all applicable fields with the name inside the form).
  * @param {any} value
  * @param {string|null} [type=null] A field type hint for scenarios where it is not implicitly clear from other arguments, e.g. "checkboxes" with a string `target`.
  * @param {((control: import("$lib/core/js/util/defs").FormControl) => boolean)|null} [filter=null] All elements for which this method returns false will be skipped.
  */
export function loadField(form, target, value, type = null, filter = null) {
  if (value == null) return;

  const t = /** @type {import("$lib/core/js/util/defs").FormControl|string} */ (target);

  if (typeof(t) === "string") {
    const name = t;

    if (type === "checkboxes" && Array.isArray(value)) {
      loadCheckboxes(form, name, /** @type {string[]} */ (value));
    } else {
      for (const control of getNamedControls(form, name, filter)) {
        loadField(form, control, value, null);
      }
    }
  } else {
    const element = t;

    if (filter && filter(element) === false) return;

    if (element.type === "checkbox") {
      element.checked = indicator2Bool(value);
    } else if (element instanceof HTMLSelectElement && element.options.length === 3 && element.options.item(1)?.textContent === "Yes") {
      updateYesNoSelect(element, value);
    } else {
      element.value = value;
    }
      
    const ev = new Event("change", {bubbles: true, cancelable: true});
    element.dispatchEvent(ev);
  }
}

/**
  * @param {HTMLFormElement} form
  * @param {string} form
  * @param {string} name
  * @param {string[]|undefined|null} values
  */
function loadCheckboxes(form, name, values) {
    const elements = getNamedControls(form, name, isCheckbox);

    // Clear existing values.
    elements.forEach(el => el.checked = false);

    const seq = /** @type {string[]|null|undefined} */ (values);
    if (!Array.isArray(seq)) return;

    // "Check" all checkboxes that match the prefill data for that code.
    for (const value of seq) {
        for (const el of elements) {
            if (el.value === value) {
                el.checked = true;
                break;
            }

        }
    }
}
